import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import format from 'date-fns/format';
import { toDate } from 'date-fns-tz';
import { useParams } from 'react-router-dom';
import history from 'routing/history';
import Button, { BackButton } from 'components/Button';
import Tooltip from 'components/Tooltip';
import { CountrySelect, TextInput, Select, MoneyInput } from 'components/Inputs';
import useCurrencies from 'products/nomad_insurance/hooks/useCurrencies';
import useCurrentContract from 'products/nomad_insurance/hooks/useCurrentContract';
import Heading from './components/Heading';
import ClaimTypeIcon from './components/ClaimTypeIcon';
import DateLabel from './components/DateLabel';
import ViewAnimator from '../../components/ViewAnimator';
import EditClaimError from '../../components/EditClaimError';
import useNavigateToNextStep from '../../hooks/useNavigateToNextStep';
import useNextAndPrevUrl from '../../hooks/useNextAndPrevUrl';
import validation from './validation';
import {
  ClaimHeading,
  FieldLabel,
  EventDate,
  Row,
  InputRow,
  StyledAlert
} from './styled';
import { NavigationButtons, Field } from '../../styled';
import { isDateWithinContracts, getClaimAmountInUsd } from '../../helpers';
import {
  setClaimInfo,
  editClaim,
  addValidStep,
  removeValidStep,
  setIdCheckNeeded,
  setClaimOverThousandUsd,
  fetchContractBasedOnDateOfService
} from '../../state';
import { STEP_PATHS } from '../../consts';
import ContractIDError from './components/ContractIDError';

const Info = () => {
  const dispatch = useDispatch();
  const { claimType, step } = useParams();
  const { incidentCountry, incidentCity, claimAmount, claimCurrency, dateOfService } =
    useSelector((state) => state.nomadInsurance.claims.claimInfo);
  const { claimID, editError, isLoading, contractID } = useSelector(
    (state) => state.nomadInsurance.claims
  );
  const { swContracts: contract } = useCurrentContract();
  const { nextUrl, prevUrl } = useNextAndPrevUrl(claimType, step);
  useNavigateToNextStep(nextUrl, editError);
  const currencies = useCurrencies();

  const formik = useFormik({
    initialValues: useMemo(
      () => ({
        incidentCountry,
        incidentCity,
        claimAmount,
        claimCurrency,
        dateOfService
      }),
      [incidentCountry, incidentCity, claimAmount, claimCurrency, dateOfService] // eslint-disable-line
    ),
    onSubmit: async (editedInfo) => {
      let claimAmount = editedInfo.claimAmount;
      if (editedInfo.claimCurrency !== 'USD') {
        claimAmount = await getClaimAmountInUsd(editedInfo.claimCurrency, claimAmount);
      }
      dispatch(setClaimOverThousandUsd(false));
      dispatch(setIdCheckNeeded(false));

      try {
        await dispatch(
          fetchContractBasedOnDateOfService(editedInfo.dateOfService)
        ).unwrap();
      } catch {
        return;
      }

      if (claimID) {
        await dispatch(editClaim({ claimID, editedInfo }));
      } else {
        dispatch(setClaimInfo(editedInfo));
        history.push(nextUrl);
      }
    },
    validateOnMount: true,
    validationSchema: validation
  });

  useEffect(() => {
    if (formik.isValid) {
      dispatch(addValidStep(STEP_PATHS.TRAVEL_INFORMATION));
    } else {
      dispatch(removeValidStep(STEP_PATHS.TRAVEL_INFORMATION));
    }
  }, [formik.isValid, dispatch]);

  return (
    <ViewAnimator>
      <ClaimTypeIcon claimType={claimType} />
      <Heading claimType={claimType} />
      <ClaimHeading>{`Your ${claimType} claim`}</ClaimHeading>
      <Field>
        <DateLabel claimType={claimType} />
        <EventDate
          selectedDate={
            formik.values.dateOfService &&
            toDate(formik.values.dateOfService, {
              timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
            })
          }
          onDateSelected={(val) => {
            formik.setFieldValue('dateOfService', format(val, 'yyyy-MM-dd'));
          }}
          maxDate={new Date()}
          filterDate={(date) => isDateWithinContracts(date, contract)}
          variant="turquoise"
          name="dateOfService"
        />
      </Field>
      <Row>
        <Field>
          <FieldLabel>Country</FieldLabel>
          <CountrySelect formik={formik} name="incidentCountry" variant="outline" />
        </Field>
        <Field>
          <b>City</b>
          <TextInput formik={formik} name="incidentCity" />
        </Field>
      </Row>
      <Field>
        <span>
          <b>Claim amount</b> (in currency of the bill)
          <Tooltip variant="grey">
            If your claim does not have an invoice, bill, or receipt associated with it by
            nature, such as accidental death/dismemberment or COVID-19 quarantine
            indemnity, please enter the amount you wish to claim in your preferred
            currency.
          </Tooltip>
        </span>
        <InputRow>
          <Select
            variant="outline"
            formik={formik}
            name="claimCurrency"
            options={currencies}
          />
          <MoneyInput
            onChange={(val) => formik.setFieldValue('claimAmount', val)}
            formik={formik}
            value={formik.values.claimAmount}
            name="claimAmount"
          />
        </InputRow>
      </Field>
      <EditClaimError error={editError} isVisible={editError} />
      <ContractIDError error={contractID.error} isVisible={contractID.error} />
      {formik.errors.claimAmount && formik.values.claimAmount && (
        <StyledAlert variant="warning" label="Invalid Claim Amount">
          The provided claim amount must be less than 12 digits not including decimals.
        </StyledAlert>
      )}

      <NavigationButtons>
        <BackButton onClick={() => history.push(prevUrl)} variant="dark">
          Back
        </BackButton>
        <Button
          onClick={() => formik.submitForm()}
          disabled={!formik.isValid || isLoading || contractID.isLoading}
        >
          Save & next
        </Button>
      </NavigationButtons>
    </ViewAnimator>
  );
};

export default Info;
